import { nanoid } from "nanoid";

// Emoji tags for different types of operations
export const LOG_TAGS = {
  SESSION_SAVE: '💾',
  SESSION_MODAL: '🎭',
  SESSION_FORM: '📝',
  LISTING_DELETE: '🗑️',
  LISTING_STATUS: '🏷️',
  LISTING_CARD: '🃏',
  API: '🌐',
  AUTH: '🔐',
  CACHE: '🗄️',
  VALIDATION: '✅',
  ERROR: '❌',
  SUCCESS: '✅',
  WARNING: '⚠️',
  INFO: 'ℹ️'
} as const;

export type LogTag = keyof typeof LOG_TAGS;

interface LogContext {
  correlationId: string;
  userId?: string;
  sessionId?: string;
  listingId?: string;
  component?: string;
  action?: string;
  [key: string]: any;
}

class DebugLogger {
  private contexts: Map<string, LogContext> = new Map();
  private isDevelopment = false;

  /**
   * Create a new logging context with a correlation ID
   */
  createContext(contextId: string, initialData: Partial<LogContext> = {}): string {
    const correlationId = nanoid(8);
    const context: LogContext = {
      correlationId,
      timestamp: new Date().toISOString(),
      ...initialData
    };
    
    this.contexts.set(contextId, context);
    
    if (this.isDevelopment) {
      console.log(`🆔 [${correlationId}] Context created: ${contextId}`, context);
    }
    
    return correlationId;
  }

  /**
   * Update existing context with new data
   */
  updateContext(contextId: string, updates: Partial<LogContext>): void {
    const existing = this.contexts.get(contextId);
    if (existing) {
      this.contexts.set(contextId, { ...existing, ...updates });
    }
  }

  /**
   * Get correlation ID for adding to API headers
   */
  getCorrelationId(contextId: string): string | undefined {
    return this.contexts.get(contextId)?.correlationId;
  }

  /**
   * Main logging method with grouped output
   */
  log(
    tag: LogTag,
    message: string,
    data?: any,
    contextId?: string,
    groupCollapsed = true
  ): void {
    if (!this.isDevelopment) return;

    const emoji = LOG_TAGS[tag];
    const context = contextId ? this.contexts.get(contextId) : undefined;
    const correlationId = context?.correlationId || 'no-context';
    
    const groupTitle = `${emoji} [${correlationId}] ${tag}: ${message}`;

    if (groupCollapsed) {
      console.groupCollapsed(groupTitle);
    } else {
      console.group(groupTitle);
    }

    // Log timestamp
    console.log(`⏰ Timestamp: ${new Date().toISOString()}`);

    // Log context if available
    if (context) {
      console.log('📋 Context:', this.sanitizeData(context));
    }

    // Log the main data
    if (data !== undefined) {
      if (typeof data === 'object' && data !== null) {
        console.log('📊 Data:', this.sanitizeData(data));
      } else {
        console.log('📊 Data:', data);
      }
    }

    // Log stack trace for errors
    if (tag === 'ERROR') {
      console.trace('Stack trace:');
    }

    console.groupEnd();
  }

  /**
   * Log form lifecycle events
   */
  logFormLifecycle(
    phase: 'mount' | 'submit' | 'validation' | 'error' | 'success',
    formName: string,
    data?: any,
    contextId?: string
  ): void {
    const tag = phase === 'error' ? 'ERROR' : 'SESSION_FORM';
    this.log(tag, `Form ${phase}: ${formName}`, data, contextId);
  }

  /**
   * Log modal lifecycle events
   */
  logModalLifecycle(
    event: 'open' | 'close' | 'mount' | 'unmount',
    modalName: string,
    data?: any,
    contextId?: string
  ): void {
    this.log('SESSION_MODAL', `Modal ${event}: ${modalName}`, data, contextId);
  }

  /**
   * Log API requests and responses
   */
  logApiCall(
    method: string,
    endpoint: string,
    data?: {
      payload?: any;
      response?: any;
      error?: any;
      headers?: Record<string, string>;
      queryKey?: any;
      duration?: number;
    },
    contextId?: string
  ): void {
    const tag = data?.error ? 'ERROR' : 'API';
    this.log(tag, `${method} ${endpoint}`, {
      method,
      endpoint,
      ...data
    }, contextId);
  }

  /**
   * Log cache operations
   */
  logCacheOperation(
    operation: 'invalidate' | 'update' | 'fetch' | 'miss' | 'hit',
    queryKey: any,
    data?: any,
    contextId?: string
  ): void {
    this.log('CACHE', `Cache ${operation}`, {
      operation,
      queryKey: this.sanitizeQueryKey(queryKey),
      ...data
    }, contextId);
  }

  /**
   * Log validation results
   */
  logValidation(
    success: boolean,
    schemaName: string,
    data?: {
      input?: any;
      output?: any;
      errors?: any;
    },
    contextId?: string
  ): void {
    const tag = success ? 'VALIDATION' : 'ERROR';
    this.log(tag, `Validation ${success ? 'passed' : 'failed'}: ${schemaName}`, {
      success,
      schema: schemaName,
      ...data
    }, contextId);
  }

  /**
   * Log authentication state
   */
  logAuth(
    event: string,
    data?: {
      userId?: string;
      authenticated?: boolean;
      token?: string;
      error?: any;
    },
    contextId?: string
  ): void {
    const sanitizedData = data ? {
      ...data,
      token: data.token ? `${data.token.substring(0, 10)}...` : undefined
    } : undefined;
    
    this.log('AUTH', event, sanitizedData, contextId);
  }

  /**
   * Clean up expired contexts to prevent memory leaks
   */
  cleanupContexts(): void {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    
    for (const [contextId, context] of this.contexts.entries()) {
      const contextTime = new Date(context.timestamp || 0).getTime();
      if (contextTime < oneHourAgo) {
        this.contexts.delete(contextId);
      }
    }
  }

  /**
   * Sanitize data to prevent logging of sensitive information
   */
  private sanitizeData(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    const sensitiveKeys = [
      'password', 'token', 'secret', 'key', 'auth', 'authorization',
      'cookie', 'session', 'csrf', 'api_key', 'access_token', 'refresh_token'
    ];

    const sanitized = { ...data };

    for (const key of Object.keys(sanitized)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        if (typeof sanitized[key] === 'string' && sanitized[key].length > 10) {
          sanitized[key] = `${sanitized[key].substring(0, 10)}...`;
        } else {
          sanitized[key] = '[REDACTED]';
        }
      }
    }

    // Truncate large objects/arrays to prevent log bloat
    if (JSON.stringify(sanitized).length > 10000) {
      return { ...sanitized, _truncated: 'Large object truncated for logging' };
    }

    return sanitized;
  }

  /**
   * Sanitize query keys for logging
   */
  private sanitizeQueryKey(queryKey: any): any {
    if (Array.isArray(queryKey)) {
      return queryKey.map(key => 
        typeof key === 'string' && key.length > 100 
          ? `${key.substring(0, 100)}...` 
          : key
      );
    }
    return queryKey;
  }
}

// Create singleton instance
export const debugLogger = new DebugLogger();

// Auto-cleanup contexts every 30 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    debugLogger.cleanupContexts();
  }, 30 * 60 * 1000);
}

// Convenience functions for common operations
export const logSessionSave = (message: string, data?: any, contextId?: string) => 
  debugLogger.log('SESSION_SAVE', message, data, contextId);

export const logListingDelete = (message: string, data?: any, contextId?: string) => 
  debugLogger.log('LISTING_DELETE', message, data, contextId);

export const logError = (message: string, error?: any, contextId?: string) => 
  debugLogger.log('ERROR', message, error, contextId);

export const logSuccess = (message: string, data?: any, contextId?: string) => 
  debugLogger.log('SUCCESS', message, data, contextId);

export default debugLogger;